import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { blogService, BlogPost } from '../../services/blogService';

const EditorContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const EditorCard = styled(motion.div)`
  background: rgba(26, 26, 26, 0.95);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const EditorTitle = styled.h2`
  color: white;
  font-size: 2rem;
  margin-bottom: 2rem;
  font-family: 'kenpixel', 'Press Start 2P', 'VT323', 'Share Tech Mono', 'Orbitron', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.6);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const Label = styled.label`
  color: white;
  font-family: "Handjet", sans-serif;
  font-optical-sizing: auto;
  font-weight: 400;
  font-variation-settings:
    "ELGR" 1,
    "ELSH" 2;
  font-size: 1.3rem;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.6);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const TextArea = styled.textarea`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  min-height: 200px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.6);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const Select = styled.select`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.6);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
  }

  option {
    background: #1a1a1a;
    color: white;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled(motion.button)<{ variant?: 'primary' | 'secondary' }>`
  padding: 1rem 2rem;
  border: 2px solid ${props => props.variant === 'primary' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  background: ${props => props.variant === 'primary' ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  font-family: "Handjet", sans-serif;
  font-optical-sizing: auto;
  font-weight: 500;
  font-variation-settings:
    "ELGR" 1,
    "ELSH" 2;

  &:hover {
    background: ${props => props.variant === 'primary' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
    border-color: rgba(255, 255, 255, 0.6);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormattingToolbar = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const FormatButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ImageUploadButton = styled.label`
  padding: 0.5rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  input[type="file"] {
    display: none;
  }
`;

const ImagePreview = styled.div`
  margin: 1rem 0;
  padding: 1rem;
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  text-align: center;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
  margin-bottom: 0.5rem;
`;

const ImageInfo = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const ImageActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
`;

const SmallButton = styled.button`
  padding: 0.3rem 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const InsertButton = styled(SmallButton)`
  background: rgba(0, 191, 255, 0.2);
  border-color: rgba(0, 191, 255, 0.4);

  &:hover {
    background: rgba(0, 191, 255, 0.3);
  }
`;

const RemoveButton = styled(SmallButton)`
  background: rgba(255, 107, 107, 0.2);
  border-color: rgba(255, 107, 107, 0.4);

  &:hover {
    background: rgba(255, 107, 107, 0.3);
  }
`;

const ImageLibraryModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 2rem;
  backdrop-filter: blur(5px);
`;

const ImageLibraryContent = styled.div`
  background: rgba(26, 26, 26, 0.95);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  backdrop-filter: blur(10px);
`;

const ImageLibraryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const ImageLibraryTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  font-family: "Handjet", sans-serif;
  font-optical-sizing: auto;
  font-weight: 500;
  font-variation-settings:
    "ELGR" 1,
    "ELSH" 2;
`;

const CloseButton = styled.button`
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 4px;
  color: #ff6b6b;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.3);
  }
`;

const ImageLibraryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
`;

const LibraryImageCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(0, 191, 255, 0.5);
    background: rgba(0, 191, 255, 0.1);
  }
`;

const LibraryImagePreview = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 0.5rem;
`;

const LibraryImageInfo = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;
  font-family: "Handjet", sans-serif;
  font-optical-sizing: auto;
  font-weight: 300;
  font-variation-settings:
    "ELGR" 1,
    "ELSH" 2;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: rgba(81, 207, 102, 0.2);
  border: 1px solid rgba(81, 207, 102, 0.4);
  border-radius: 8px;
  color: #51cf66;
  padding: 1rem;
  margin-bottom: 1rem;
  font-family: "Handjet", sans-serif;
  font-optical-sizing: auto;
  font-weight: 400;
  font-variation-settings:
    "ELGR" 1,
    "ELSH" 2;
  text-align: center;
`;

const PreviewContainer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  max-height: 300px;
  overflow-y: auto;
`;

const PreviewTitle = styled.h4`
  color: white;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-family: "Handjet", sans-serif;
  font-optical-sizing: auto;
  font-weight: 500;
  font-variation-settings:
    "ELGR" 1,
    "ELSH" 2;
`;

const PreviewContent = styled.div`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1rem 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  p {
    margin: 1rem 0;
  }
  
  strong {
    color: #0080ff;
    font-weight: 600;
  }
  
  em {
    color: #00bfff;
    font-style: italic;
  }
`;

interface TestBlogEditorProps {
  onSave?: (post: any) => void;
  onCancel?: () => void;
}

const TestBlogEditor: React.FC<TestBlogEditorProps> = ({ onSave, onCancel }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('street');
  const [tags, setTags] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [boldActive, setBoldActive] = useState(false);
  const [italicActive, setItalicActive] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageAlt, setImageAlt] = useState('');
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [storedImages, setStoredImages] = useState<any[]>([]);
  const [saveMessage, setSaveMessage] = useState<string>('');

  // Function to convert image placeholders to markdown for saving
  const convertImagePlaceholdersToMarkdown = (content: string): string => {
    return content.replace(/\[IMAGE:([^:]+):([^\]]+)\]/g, (match, imageId, altText) => {
      const image = blogService.getImageById(imageId);
      if (image) {
        return `![${altText}](${image.url})`;
      }
      return `![${altText}](image-not-found)`;
    });
  };

  // Function to render content preview with images
  const renderContentPreview = (content: string): JSX.Element => {
    const parts = content.split(/(\[IMAGE:[^:]+:[^\]]+\])/g);
    
    return (
      <PreviewContent>
        {parts.map((part, index) => {
          if (part.startsWith('[IMAGE:') && part.endsWith(']')) {
            const match = part.match(/\[IMAGE:([^:]+):([^\]]+)\]/);
            if (match) {
              const [, imageId, altText] = match;
              const image = blogService.getImageById(imageId);
              if (image) {
                return (
                  <img 
                    key={index} 
                    src={image.url} 
                    alt={altText} 
                    style={{ 
                      maxWidth: '100%', 
                      height: 'auto', 
                      borderRadius: '8px', 
                      margin: '1rem 0',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }} 
                  />
                );
              }
            }
            return <span key={index} style={{ color: '#ff6b6b' }}>[Image not found]</span>;
          }
          
          // Handle other markdown formatting
          let formattedPart = part;
          formattedPart = formattedPart.replace(/\*\*(.*?)\*\*/g, '<strong style="color: #0080ff; font-weight: 600;">$1</strong>');
          formattedPart = formattedPart.replace(/\*(.*?)\*/g, '<em style="color: #00bfff; font-style: italic;">$1</em>');
          formattedPart = formattedPart.replace(/^### (.*$)/gm, '<h3 style="color: #00bfff; margin: 1.5rem 0 0.8rem 0; font-size: 1.4rem;">$1</h3>');
          formattedPart = formattedPart.replace(/^## (.*$)/gm, '<h2 style="color: #0080ff; margin: 2rem 0 1rem 0; font-size: 1.8rem;">$1</h2>');
          
          if (formattedPart.trim()) {
            return <div key={index} dangerouslySetInnerHTML={{ __html: formattedPart }} />;
          }
          return null;
        })}
      </PreviewContent>
    );
  };

  const categories = [
    { value: 'street', label: 'Street Photography' },
    { value: 'fashion', label: 'Fashion' },
    { value: 'technique', label: 'Technique' },
    { value: 'events', label: 'Events' },
    { value: 'portraits', label: 'Portraits' }
  ];

  // Load stored images when component mounts
  useEffect(() => {
    const images = blogService.getAllImages();
    setStoredImages(images);
  }, []);

  // Prevent body scrolling when image library modal is open and handle escape key
  useEffect(() => {
    if (showImageLibrary) {
      document.body.style.overflow = 'hidden';
      
      // Handle escape key to close modal
      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setShowImageLibrary(false);
        }
      };
      
      document.addEventListener('keydown', handleEscapeKey);
      
      // Cleanup function
      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscapeKey);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showImageLibrary]);

  const insertStoredImageIntoContent = (image: any) => {
    const textarea = document.getElementById('content') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    // Create image placeholder with the stored image ID
    const imagePlaceholder = `\n\n[IMAGE:${image.id}:${image.alt}]\n\n`;
    
    const newContent = content.substring(0, start) + imagePlaceholder + content.substring(end);
    setContent(newContent);

    // Close image library
    setShowImageLibrary(false);
    
    // Focus back to textarea
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + imagePlaceholder.length, start + imagePlaceholder.length);
    }, 100);
  };

  const handleSave = async () => {
    setIsLoading(true);
    
    try {
      // Convert image placeholders to markdown for saving
      const markdownContent = convertImagePlaceholdersToMarkdown(content);
      
      // Prepare post data
      const postData = {
        title,
        content: markdownContent,
        excerpt,
        category: category as BlogPost['category'],
        tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        date: new Date().toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        readTime: Math.ceil(markdownContent.split(' ').length / 200) + ' min read',
        imageIds: [] // Images are already embedded in content as markdown
      };

      // Save to blog service (now async)
      console.log('Saving post with content:', content);
      const savedPost = await blogService.addPost(postData);
      
      console.log('Blog post saved successfully:', savedPost);
      
      // Show success message
      setSaveMessage('✅ Post saved successfully! Redirecting to dashboard...');
      
      // Call onSave callback to notify parent component
      if (onSave) {
        onSave(savedPost);
      }
      
      // Reset form and redirect after a short delay
      setTimeout(() => {
        setTitle('');
        setContent('');
        setExcerpt('');
        setCategory('street');
        setTags('');
        setSelectedImage(null);
        setImagePreview(null);
        setImageAlt('');
        setSaveMessage('');
        
        // Navigate back to dashboard
        if (onCancel) {
          onCancel();
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error saving blog post:', error);
      setSaveMessage('❌ Error saving blog post. Please try again.');
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const insertFormatting = (format: string) => {
    const textarea = document.getElementById('content') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        setBoldActive(!boldActive);
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        setItalicActive(!italicActive);
        break;
      case 'header':
        formattedText = `## ${selectedText}`;
        break;
      default:
        formattedText = selectedText;
    }
    
    const newContent = content.substring(0, start) + formattedText + content.substring(end);
    setContent(newContent);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image file size must be less than 5MB.');
      return;
    }

    setSelectedImage(file);
    setImageAlt(file.name.split('.')[0]); // Use filename as default alt text

    // Create preview URL
    const reader = new FileReader();
    reader.onload = async (e) => {
      setImagePreview(e.target?.result as string);
      
      // Automatically insert the image into the post content
      await autoInsertImageIntoContent(file, file.name.split('.')[0]);
    };
    reader.readAsDataURL(file);
  };

  const autoInsertImageIntoContent = async (file: File, altText: string) => {
    const textarea = document.getElementById('content') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    try {
      // Show loading state
      setIsLoading(true);
      
      // Upload image to file storage first
      const imageId = await blogService.uploadImage(file, altText);
      const storedImage = blogService.getImageById(imageId);
      
      if (storedImage) {
        // Insert image using a special placeholder that we'll render as an actual image
        const imagePlaceholder = `\n\n[IMAGE:${storedImage.id}:${altText}]\n\n`;
        console.log('Inserting image placeholder:', imagePlaceholder);
        console.log('Stored image URL:', storedImage.url);
        
        const newContent = content.substring(0, start) + imagePlaceholder + content.substring(end);
        setContent(newContent);
        console.log('New content after insertion:', newContent);

        // Clear image selection after successful insertion
        setSelectedImage(null);
        setImagePreview(null);
        setImageAlt('');
        
        // Focus back to textarea
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + imagePlaceholder.length, start + imagePlaceholder.length);
        }, 100);
        
        // Show success message
        setSaveMessage('Image uploaded and inserted successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
        
        // Update stored images list
        const images = blogService.getAllImages();
        setStoredImages(images);
        
      } else {
        throw new Error('Failed to retrieve uploaded image');
      }
    } catch (error) {
      console.error('Error uploading and inserting image:', error);
      setSaveMessage('Error uploading image. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const insertImageIntoContent = async () => {
    if (!selectedImage || !imagePreview) return;
    await autoInsertImageIntoContent(selectedImage, imageAlt);
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setImageAlt('');
  };

  return (
    <EditorContainer>
      <EditorCard
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <EditorTitle>Create New Blog Post (Test Editor)</EditorTitle>
        
        {saveMessage && <SuccessMessage>{saveMessage}</SuccessMessage>}
        
        <Form>
          <FormGroup>
            <Label htmlFor="title">Post Title</Label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter your blog post title"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="excerpt">Excerpt</Label>
            <Input
              id="excerpt"
              type="text"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Brief description of your post"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="category">Category</Label>
            <Select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="tags">Tags (comma-separated)</Label>
            <Input
              id="tags"
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Photography, Street, São Paulo"
            />
          </FormGroup>

          <FormGroup>
            <Label>Content</Label>
            <FormattingToolbar>
              <FormatButton 
                active={boldActive}
                onClick={() => insertFormatting('bold')}
              >
                Bold
              </FormatButton>
              <FormatButton 
                active={italicActive}
                onClick={() => insertFormatting('italic')}
              >
                Italic
              </FormatButton>
              <FormatButton onClick={() => insertFormatting('header')}>
                Header
              </FormatButton>
              <FormatButton onClick={() => setShowImageLibrary(true)}>
                🖼️ Image Library
              </FormatButton>
              <ImageUploadButton>
                📷 Upload New Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </ImageUploadButton>
            </FormattingToolbar>
            <TextArea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your blog post content here..."
              required
            />
            
            {/* Image Upload Status */}
            {isLoading && (
              <ImagePreview>
                <div style={{ color: 'white', textAlign: 'center', padding: '2rem' }}>
                  <div>Uploading and inserting image...</div>
                </div>
              </ImagePreview>
            )}
            
            {/* Content Preview */}
            {content && (
              <PreviewContainer>
                <PreviewTitle>Preview:</PreviewTitle>
                {renderContentPreview(content)}
              </PreviewContainer>
            )}
          </FormGroup>

          <ButtonGroup>
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Back to Dashboard
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSave}
              disabled={!title || !content || !excerpt || isLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isLoading ? 'Saving...' : 'Save Post'}
            </Button>
          </ButtonGroup>
        </Form>
      </EditorCard>

      {/* Image Library Modal */}
      {showImageLibrary && (
        <ImageLibraryModal 
          onClick={(e) => {
            // Close modal when clicking outside the content area
            if (e.target === e.currentTarget) {
              setShowImageLibrary(false);
            }
          }}
        >
          <ImageLibraryContent
            onClick={(e) => {
              // Prevent event bubbling to prevent modal from closing when clicking inside content
              e.stopPropagation();
            }}
          >
            <ImageLibraryHeader>
              <ImageLibraryTitle>Select Image from Library</ImageLibraryTitle>
              <CloseButton 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowImageLibrary(false);
                }}
              >
                Close
              </CloseButton>
            </ImageLibraryHeader>
            
            {storedImages.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)', padding: '2rem' }}>
                No images in library. Upload some images first!
              </div>
            ) : (
              <ImageLibraryGrid>
                {storedImages.map((image) => (
                  <LibraryImageCard 
                    key={image.id} 
                    onClick={(e) => {
                      e.stopPropagation();
                      insertStoredImageIntoContent(image);
                    }}
                  >
                    <LibraryImagePreview src={image.url} alt={image.alt} />
                    <LibraryImageInfo>
                      <div><strong>{image.originalName}</strong></div>
                      <div>Click to insert</div>
                    </LibraryImageInfo>
                  </LibraryImageCard>
                ))}
              </ImageLibraryGrid>
            )}
          </ImageLibraryContent>
        </ImageLibraryModal>
      )}
    </EditorContainer>
  );
};

export default TestBlogEditor;
